// deps
import React, { ReactElement, useMemo } from "react";
import classNames from "classnames";

// internal deps
import { AriaHidden, FormatTextToHtml } from "@swiggy-private/core-ui";

// constants
import { SAVINGS_BANNER_TEST_IDS } from "@super-ui/test-ids/savings-banner";

// helpers
import { getFormattedNumber } from "@super-ui/helpers/String";
import { PRE_CONFIGURED_FILLERS } from "@super-ui/constants/SwiggyOne";
import CartSavingsBannerOneLineDiscountCounter from "./CartSavingsBannerOneLineDiscountCounter";

//styles
import styles from "../styles/CartSavingsBannerOneLine.scss";

interface CartSavingsBannerOneLineTextContainer {
    title: string;
    subtitle: string;
    details: Record<string, number>;
    isBannerSticky: boolean;
    wrapperClass?: string;
    leadingIcon?: string;
    isCartV2?: boolean;
    isGiftingCartFlow?: boolean;
}

const CartSavingsBannerOneLineTextContainer: React.FC<CartSavingsBannerOneLineTextContainer> = ({
    title,
    subtitle,
    details,
    wrapperClass,
    isBannerSticky,
    isCartV2,
    isGiftingCartFlow,
}) => {
    const renderTitle: ReactElement = useMemo(() => {
        let heading = title;

        if (isGiftingCartFlow) {
            return <span className={classNames(styles.title, styles.titleGiftingCart)}>{heading.trim()}</span>;
        }

        // 1. search for "{{total_discount}}"
        const searchString = `{{${PRE_CONFIGURED_FILLERS.TOTAL_DISCOUNT}}}`;

        // 2. Identify position {{total_discount}} in the string "{{total_discount}}xyz", position: 0
        const position = heading.indexOf(searchString);
        if (position === -1) {
            return <span className={styles.title}>{heading}</span>;
        }

        // 3. remove {{total_discount}} from string: "{{total_discount}}xyz" -> xyz"
        heading = heading.replace(searchString, "");

        //4. Combine TotalDiscountCounter with heading: "<TotalDiscountCounter /> <span>xyz</span>"
        return (
            <>
                <CartSavingsBannerOneLineDiscountCounter
                    totalDiscount={details[PRE_CONFIGURED_FILLERS.TOTAL_DISCOUNT]}
                    key="p1"
                />
                <span key="p2">{heading.trim()}</span>
            </>
        );
    }, [title, isGiftingCartFlow, details]);

    const renderSubtitle: string = useMemo(() => {
        // replace keys from "details" by their values in "subtitle"
        const replaceableKeys = Object.keys(details);
        let innerHTML = subtitle;
        for (const key of replaceableKeys) {
            const searchString = `{{${key}}}`;
            if (key === PRE_CONFIGURED_FILLERS.SUB_DISCOUNT) {
                innerHTML = innerHTML.replace(
                    new RegExp(searchString, "g"),
                    `<span style="font-weight: 600;" class="${styles.rupee}">${getFormattedNumber(
                        details[key],
                    )}</span>`,
                );
            }
        }
        // add image support to subtitle
        return new FormatTextToHtml(innerHTML).getImage({ imgStyle: styles.titleImage }).val;
    }, [details, subtitle]);

    const wrapperStyle: string = useMemo(() => {
        return classNames(wrapperClass ? wrapperClass : styles.wrapper, {
            [styles.giftingCartWrapper]: isGiftingCartFlow,
        });
    }, [wrapperClass, isGiftingCartFlow]);

    const titleClassName: string = useMemo(() => {
        return classNames(isBannerSticky ? styles.titleSticky : styles.title, {
            [styles.titleStickyCartV2]: isCartV2,
        });
    }, [isBannerSticky, isCartV2]);

    const subtitleClassName: string = useMemo(() => {
        return classNames(isBannerSticky ? styles.subtitleSticky : styles.subtitle, {
            [styles.subtitleStickyCartV2]: isCartV2,
        });
    }, [isBannerSticky, isCartV2]);

    return (
        <AriaHidden as="div" className={styles.containerForText}>
            <div className={wrapperStyle}>
                <div className={titleClassName} data-testid={SAVINGS_BANNER_TEST_IDS.TEXT}>
                    {renderTitle}
                </div>
                <div>&nbsp;</div>
                <div
                    className={subtitleClassName}
                    dangerouslySetInnerHTML={{ __html: renderSubtitle }}
                    data-testid={SAVINGS_BANNER_TEST_IDS.SUBTEXT}
                />
            </div>
        </AriaHidden>
    );
};

export default React.memo(CartSavingsBannerOneLineTextContainer);
